import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { saveData } from 'services/api/saveData';
import { AudienceCondition, AudiencePreviewRecipient, AudienceSource } from './NewsletterTypes';

interface NewsletterAudiencePreviewProps {
    conditions: AudienceCondition[];
    source: AudienceSource;
}

// Shows who is in an audience right now: the count and the resolved recipients, loaded per page
const NewsletterAudiencePreview: React.FC<NewsletterAudiencePreviewProps> = ({ conditions, source }) => {
    const { t } = useTranslation();
    const [recipients, setRecipients] = useState<AudiencePreviewRecipient[]>([]);
    const [count, setCount] = useState<number | null>(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const latestRequestRef = useRef(0);

    // Loads a page of the resolved recipients, the first page replaces the list
    const loadPage = async (requestedPage: number) => {
        const requestId = ++latestRequestRef.current;
        setLoading(true);
        try {
            const response = await saveData({ apiUrl: 'post_newsletter_audience_preview', method: 'post', data: { audience_conditions: conditions, source, page: requestedPage } });
            if (requestId !== latestRequestRef.current) return;
            const data = response?.data;
            setCount(data?.count ?? 0);
            setPageSize(data?.page_size ?? 50);
            setPage(requestedPage);
            setRecipients(current => (requestedPage === 1 ? data?.results ?? [] : [...current, ...(data?.results ?? [])]));
            setError(null);
        } catch (requestError: any) {
            if (requestId !== latestRequestRef.current) return;
            setCount(null);
            setRecipients([]);
            setError(requestError?.response?.data?.audience_conditions ?? requestError?.response?.data?.general ?? 'newsletters.audience.count_failed');
        } finally {
            if (requestId === latestRequestRef.current) setLoading(false);
        }
    };

    // Reload the first page a moment after the conditions change
    useEffect(() => {
        const timer = setTimeout(() => { loadPage(1); }, 600);
        return () => clearTimeout(timer);
    }, [conditions, source]);

    // Whether more recipients can be loaded
    const hasMore = count !== null && recipients.length < count;

    return (
        <div className='nl-audience-preview'>
            <h6 className='newsletter-card-header'>{t('newsletters.audiences.preview_title')}</h6>
            <p className='newsletter-card-intro'>{t('newsletters.audiences.preview_intro')}</p>

            {error && <div className='newsletter-field-error'>{t(error)}</div>}

            {!error && (
                <div className='nl-send-recipient-list'>
                    {recipients.map(recipient => (
                        <div key={`${recipient.contact_id ?? ''}-${recipient.user_id ?? ''}-${recipient.email}`} className='nl-send-recipient'>
                            <span className='nl-send-recipient-name'>{recipient.name}</span>
                            <span className='nl-send-recipient-email'>{recipient.email}</span>
                        </div>
                    ))}
                    {count === 0 && !loading && <div className='nl-send-loading nl-audience-preview-empty'>{t('newsletters.audiences.preview_empty')}</div>}
                    {count === null && loading && <div className='nl-send-loading nl-audience-preview-empty'>{t('newsletters.audience.counting')}</div>}
                    {hasMore && (
                        <button type='button' className='nl-send-load-more' disabled={loading} onClick={() => loadPage(page + 1)}>
                            {t('newsletters.send.load_more', { shown: recipients.length, total: count, size: pageSize })}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default NewsletterAudiencePreview;
